import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { compose } from "redux";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import { useLockViewportScroll } from "../../../../hook/useLockViewportScroll";
import AuthenticationApiSerivce from "../../../../api/AuthServices";
import {
  isRegistrationLoadRetryExhaustedError,
  isRequestCancelledError,
} from "../../../../api/requestRetry";
import { RegistrationLayout } from "../../../RegistrationLayout/RegistrationLayout";
import { RegistrationSetupNavbar } from "../../../RegistrationLayout/RegistrationSetupNavbar";
import {
  REGISTRATION_STEP_TITLE_PROPS,
  useRegistrationStepFocus,
} from "../../../../hook/useRegistrationStepFocus";
import RegistrationSetupWrapper from "../../RegistrationSetup.style";
import enhancer, {
  insuranceVerificationValidationSchema,
} from "./RegistrationInsuranceVerification.validator";
import {
  REGISTRATION_SETUP_STEP_META,
  REGISTRATION_SETUP_TOTAL_STEPS,
} from "../../../../helper/constants";
import {
  getRegistrationData,
  updateRegistrationData,
} from "../../registrationSetupStorage";
import {
  INSURANCE_PROVIDER_PLACEHOLDER,
  buildInsuranceManualFromVerification,
  fetchRegistrationInsuranceProviders,
  getInsuranceOptionLabel,
  getInsuranceProviderLabel,
  RELATIONSHIP_OPTIONS,
  formatDobForDateInput,
  normalizeVerificationRelationship,
} from "../insuranceProviders";
import InsuranceDetailsConfirmModal from "../InsuranceDetailsConfirmModal/InsuranceDetailsConfirmModal";
import {
  DOB_FORMAT_VALIDATION_KEY,
  resolveRegisterValidationMessage,
} from "../../../Register/Register.validator";
import {
  getInsuranceCardImages,
  hasRequiredInsuranceCardImages,
  setInsuranceCardImages,
} from "../insuranceCardImagesStore";
import RegistrationInsuranceCardUpload from "../RegistrationInsuranceCardUpload";
import RegistrationFormSelect from "../../../RegistrationLayout/RegistrationFormSelect";
import RegistrationDobField from "../../../RegistrationLayout/RegistrationDobField";
import {
  focusFirstInvalidRegistrationField,
  getRegistrationFieldErrorId,
  RegistrationFieldError,
  scheduleFocusRegistrationField,
} from "../../../RegistrationLayout/RegistrationFieldError";
import { RegistrationRequiredMark } from "../../../RegistrationLayout/RegistrationA11y";
import { REGISTRATION_LINK } from "../../../RegistrationLayout/registrationTheme";
import clsx from "clsx";
import {
  clearInsuranceDetailsReturnToCoverage,
  registrationStepBack,
} from "../../registrationNavigation";

const INSURANCE_STEP = REGISTRATION_SETUP_STEP_META.INSURANCE_VERIFICATION;

const INSURANCE_VERIFICATION_FIELD_FOCUS_ORDER = [
  { field: "payer", id: "insurance-payer" },
  { field: "firstName", id: "insurance-first" },
  { field: "lastName", id: "insurance-last" },
  { field: "dob", id: "insurance-dob" },
  { field: "memberId", id: "insurance-member-id" },
  { field: "relationship", id: "insurance-relationship" },
  { field: "policyHolderFirstName", id: "insurance-policy-holder-first" },
  { field: "policyHolderLastName", id: "insurance-policy-holder-last" },
  { field: "policyHolderDob", id: "insurance-policy-holder-dob" },
  { field: "cardImages", selector: "#verify-insurance-card-front-upload" },
];

const RegistrationInsuranceVerification = (props) => {
  const { history } = props;
  const { t, i18n } = useTranslation();
  const stepTitleRef = useRegistrationStepFocus();
  const [insuranceProviders, setInsuranceProviders] = useState([
    INSURANCE_PROVIDER_PLACEHOLDER,
  ]);
  const [insuranceProvidersLoading, setInsuranceProvidersLoading] =
    useState(true);
  const [, setHasCardImages] = useState(() =>
    hasRequiredInsuranceCardImages()
  );
  const [cardImagesSubmitAttempted, setCardImagesSubmitAttempted] =
    useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmDetails, setConfirmDetails] = useState(null);
  const {
    setFieldValue,
    setFieldTouched,
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    submitCount,
    setTouched,
    setErrors,
    isSubmitting,
  } = props;

  useLockViewportScroll();

  useEffect(() => {
    const parsed = getRegistrationData();
    if (!parsed.firstName || !parsed.email) {
      history.replace("/register/account");
      return;
    }
    if (!parsed.dob) {
      history.replace("/register/account");
      return;
    }
    if (parsed.paymentType && parsed.paymentType !== "insurance") {
      history.replace("/register/account/payment");
      return;
    }

    if (parsed.insuranceVerification) {
      const verification = parsed.insuranceVerification;
      if (verification.payer) setFieldValue("payer", verification.payer);
      if (verification.memberId) {
        setFieldValue("memberId", verification.memberId);
      }
      if (verification.groupNumber != null) {
        setFieldValue("groupNumber", verification.groupNumber);
      }
      const relationship = normalizeVerificationRelationship(
        verification.relationship
      );
      if (relationship) {
        setFieldValue("relationship", relationship);
      }

      if (relationship === "self") {
        setFieldValue("firstName", parsed.firstName || "");
        setFieldValue("lastName", parsed.lastName || "");
        setFieldValue("dob", formatDobForDateInput(parsed.dob));
        setFieldValue("policyHolderFirstName", "");
        setFieldValue("policyHolderLastName", "");
        setFieldValue("policyHolderDob", "");
      } else if (relationship) {
        const hasPolicyHolderFields = Boolean(
          verification.policyHolderFirstName ||
            verification.policyHolderLastName ||
            verification.policyHolderDob
        );

        if (hasPolicyHolderFields) {
          setFieldValue(
            "firstName",
            verification.firstName || parsed.firstName || ""
          );
          setFieldValue(
            "lastName",
            verification.lastName || parsed.lastName || ""
          );
          setFieldValue(
            "dob",
            formatDobForDateInput(verification.dob || parsed.dob)
          );
          setFieldValue(
            "policyHolderFirstName",
            verification.policyHolderFirstName || ""
          );
          setFieldValue(
            "policyHolderLastName",
            verification.policyHolderLastName || ""
          );
          setFieldValue(
            "policyHolderDob",
            formatDobForDateInput(verification.policyHolderDob)
          );
        } else {
          // Legacy: firstName/lastName/dob stored the policy holder.
          setFieldValue("firstName", parsed.firstName || "");
          setFieldValue("lastName", parsed.lastName || "");
          setFieldValue("dob", formatDobForDateInput(parsed.dob));
          setFieldValue("policyHolderFirstName", verification.firstName || "");
          setFieldValue("policyHolderLastName", verification.lastName || "");
          setFieldValue(
            "policyHolderDob",
            formatDobForDateInput(verification.dob)
          );
        }
      }
    } else {
      setFieldValue("firstName", parsed.firstName || "");
      setFieldValue("lastName", parsed.lastName || "");
      setFieldValue("dob", formatDobForDateInput(parsed.dob));
      setFieldValue("relationship", "self");
      setFieldValue("policyHolderFirstName", "");
      setFieldValue("policyHolderLastName", "");
      setFieldValue("policyHolderDob", "");
    }

    if (Array.isArray(parsed.insuranceProviderOptions)) {
      setInsuranceProviders(parsed.insuranceProviderOptions);
    }
  }, [history, setFieldValue]);

  useEffect(() => {
    const parsed = getRegistrationData();
    const registrationCode = parsed.registrationCode?.trim();

    if (!registrationCode) {
      history.replace("/register/account");
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();
    setInsuranceProvidersLoading(true);

    fetchRegistrationInsuranceProviders({
      registrationCode,
      language: i18n.language,
      apiService: AuthenticationApiSerivce,
      signal: controller.signal,
    })
      .then(({ list, options }) => {
        if (cancelled) {
          return;
        }

        setInsuranceProviders(options);
        updateRegistrationData({
          insuranceProviderOptions: options,
          insuranceListItems: list,
        });
      })
      .catch((err) => {
        if (
          cancelled ||
          isRequestCancelledError(err) ||
          isRegistrationLoadRetryExhaustedError(err)
        ) {
          return;
        }
        message.error(
          err?.message?.startsWith?.("NUTRITION_")
            ? t(err.message)
            : err?.message || t("NUTRITION_INSDISC_ERR_FETCH_LIST")
        );
      })
      .finally(() => {
        if (!cancelled) {
          setInsuranceProvidersLoading(false);
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [history, i18n.language, t]);

  const showError = (field) => {
    const dobFieldValue = String(values[field] || "").trim();
    const isDobField = field === "dob" || field === "policyHolderDob";
    const isTypingPartialDob =
      isDobField &&
      dobFieldValue.length > 0 &&
      dobFieldValue.length < 10 &&
      /^[\d/]+$/.test(dobFieldValue);
    const hasFormatErrorOnRenderedDob =
      isDobField &&
      Boolean(dobFieldValue) &&
      !isTypingPartialDob &&
      errors[field] === DOB_FORMAT_VALIDATION_KEY;

    if (
      (errors[field] && touched[field]) ||
      submitCount > 0 ||
      hasFormatErrorOnRenderedDob
    ) {
      return (
        <RegistrationFieldError id={getRegistrationFieldErrorId(field)}>
          {resolveRegisterValidationMessage(errors[field], t)}
        </RegistrationFieldError>
      );
    }
    return null;
  };

  const isFieldInvalid = (field) => {
    const dobFieldValue = String(values[field] || "").trim();
    const isDobField = field === "dob" || field === "policyHolderDob";
    const isTypingPartialDob =
      isDobField &&
      dobFieldValue.length > 0 &&
      dobFieldValue.length < 10 &&
      /^[\d/]+$/.test(dobFieldValue);
    const hasFormatErrorOnRenderedDob =
      isDobField &&
      Boolean(dobFieldValue) &&
      !isTypingPartialDob &&
      errors[field] === DOB_FORMAT_VALIDATION_KEY;

    return Boolean(
      errors[field] &&
        (touched[field] || submitCount > 0 || hasFormatErrorOnRenderedDob)
    );
  };

  const fieldControlProps = (field, className, extra = {}) => {
    const invalid = isFieldInvalid(field);
    return {
      className: clsx(className, invalid && "form-input--invalid"),
      "aria-invalid": invalid || undefined,
      "aria-required": extra.required || undefined,
      "aria-describedby": [extra.describedBy, invalid ? getRegistrationFieldErrorId(field) : null]
        .filter(Boolean)
        .join(" ") || undefined,
    };
  };

  const showPolicyHolderFields =
    values.relationship === "spouse" || values.relationship === "dependent";

  const handleRelationshipChange = (e) => {
    const relationship = e.target.value;
    handleChange(e);

    const accountData = getRegistrationData();
    if (relationship === "self") {
      setFieldValue("firstName", accountData.firstName || "");
      setFieldValue("lastName", accountData.lastName || "");
      setFieldValue("dob", formatDobForDateInput(accountData.dob));
      setFieldValue("policyHolderFirstName", "");
      setFieldValue("policyHolderLastName", "");
      setFieldValue("policyHolderDob", "");
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    setTouched({
      payer: true,
      firstName: true,
      lastName: true,
      dob: true,
      memberId: true,
      groupNumber: true,
      relationship: true,
      policyHolderFirstName: true,
      policyHolderLastName: true,
      policyHolderDob: true,
    });

    try {
      insuranceVerificationValidationSchema.validateSync(values, {
        abortEarly: false,
      });
      setErrors({});
    } catch (validationError) {
      const formErrors = {};
      if (validationError.inner) {
        validationError.inner.forEach((error) => {
          if (error.path) {
            formErrors[error.path] = error.message;
          }
        });
        setErrors(formErrors);
      }
      window.requestAnimationFrame(() => {
        focusFirstInvalidRegistrationField(
          INSURANCE_VERIFICATION_FIELD_FOCUS_ORDER,
          formErrors
        );
      });
      return;
    }

    if (!hasRequiredInsuranceCardImages()) {
      setCardImagesSubmitAttempted(true);
      window.requestAnimationFrame(() => {
        focusFirstInvalidRegistrationField(
          INSURANCE_VERIFICATION_FIELD_FOCUS_ORDER,
          { cardImages: true }
        );
      });
      return;
    }

    const registrationData = getRegistrationData();
    if (!registrationData.dob) {
      history.replace("/register/account");
      return;
    }

    const isSelf = values.relationship === "self";
    const providerName =
      getInsuranceProviderLabel(values.payer, insuranceProviders) ||
      getInsuranceOptionLabel(
        insuranceProviders.find((p) => String(p.value) === String(values.payer)),
        t
      ) ||
      values.payer;

    setConfirmDetails({
      providerName,
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      dob: values.dob,
      memberId: values.memberId.trim(),
      groupNumber: values.groupNumber.trim(),
      relationship: values.relationship,
      policyHolderFirstName: isSelf
        ? ""
        : String(values.policyHolderFirstName ?? "").trim(),
      policyHolderLastName: isSelf
        ? ""
        : String(values.policyHolderLastName ?? "").trim(),
      policyHolderDob: isSelf ? "" : values.policyHolderDob,
    });
    setIsConfirmOpen(true);
  };

  const confirmCheckCoverage = () => {
    if (!confirmDetails) {
      return;
    }

    setInsuranceCardImages(getInsuranceCardImages());

    const insuranceVerification = {
      payer: values.payer,
      firstName: confirmDetails.firstName,
      lastName: confirmDetails.lastName,
      dob: confirmDetails.dob,
      memberId: confirmDetails.memberId,
      groupNumber: confirmDetails.groupNumber,
      relationship: confirmDetails.relationship,
      policyHolderFirstName: confirmDetails.policyHolderFirstName,
      policyHolderLastName: confirmDetails.policyHolderLastName,
      policyHolderDob: confirmDetails.policyHolderDob,
    };

    updateRegistrationData({
      insuranceVerification,
      // Keep manual in sync so a later details visit does not revive a stale DOB.
      insuranceManual: buildInsuranceManualFromVerification(insuranceVerification),
      paymentType: "insurance",
      insuranceRecheck: false,
      insuranceEligibilityResult: undefined,
      coverageResult: undefined,
    });

    setIsConfirmOpen(false);
    history.push("/register/account/insurance/checking");
  };

  const handleManualEntry = () => {
    clearInsuranceDetailsReturnToCoverage();

    const isSelf = values.relationship === "self";
    const insuranceVerification = {
      payer: values.payer,
      firstName: String(values.firstName ?? "").trim(),
      lastName: String(values.lastName ?? "").trim(),
      dob: values.dob,
      memberId: String(values.memberId ?? "").trim(),
      groupNumber: String(values.groupNumber ?? "").trim(),
      relationship: values.relationship,
      policyHolderFirstName: isSelf
        ? ""
        : String(values.policyHolderFirstName ?? "").trim(),
      policyHolderLastName: isSelf
        ? ""
        : String(values.policyHolderLastName ?? "").trim(),
      policyHolderDob: isSelf ? "" : values.policyHolderDob,
    };

    updateRegistrationData({
      insuranceVerification,
      insuranceManual: buildInsuranceManualFromVerification(insuranceVerification),
      paymentType: "insurance",
      insuranceRecheck: false,
    });

    history.push("/register/account/insurance/details");
  };

  const handleBack = () => {
    registrationStepBack(history);
  };

  return (
    <RegistrationSetupWrapper>
      <RegistrationLayout
        containScroll
        header={
          <RegistrationSetupNavbar
            stepMeta={INSURANCE_STEP}
            totalSteps={REGISTRATION_SETUP_TOTAL_STEPS}
            onBack={handleBack}
          />
        }
      >
        <div className="scroll-body">
          <div className="content-card">
            <form className="content-card-scroll" onSubmit={onSubmit} noValidate>
              <div className="insurance-hero">
                <div className="insurance-hero-icon" aria-hidden>
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={REGISTRATION_LINK}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    <polyline points="9 12 11 14 15 10" />
                  </svg>
                </div>
                <div>
                  <h2
                    className="insurance-hero-title"
                    ref={stepTitleRef}
                    {...REGISTRATION_STEP_TITLE_PROPS}
                  >
                    {t("NUTRITION_INSDISC_LBL_HEADER")}
                  </h2>
                </div>
              </div>

              <p className="section-sub">
                {t("NUTRITION_INSDISC_DESC_SUBTITLE")}
              </p>

              <div className="insurance-details-grid">
                <div className="form-group insurance-details-span-2">
                  <label className="form-label" htmlFor="insurance-payer">
                    {t("NUTRITION_COMMON_LBL_INSURANCE_PROVIDER")}{" "}
                    <RegistrationRequiredMark />
                  </label>
                  <RegistrationFormSelect
                    id="insurance-payer"
                    name="payer"
                    value={values.payer}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-busy={insuranceProvidersLoading || undefined}
                    {...fieldControlProps("payer", undefined, { required: true })}
                  >
                    {insuranceProviders.map((p) => (
                      <option key={p.value || "placeholder"} value={p.value}>
                        {getInsuranceOptionLabel(p, t)}
                      </option>
                    ))}
                  </RegistrationFormSelect>
                  {showError("payer")}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="insurance-first">
                    {t("NUTRITION_INSDISC_LBL_FIRST_NAME")}{" "}
                    <RegistrationRequiredMark />
                  </label>
                  <input
                    id="insurance-first"
                    name="firstName"
                    type="text"
                    placeholder={t("NUTRITION_INSDISC_PLACEHOLDER_FIRST_NAME")}
                    value={values.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoComplete="given-name"
                    {...fieldControlProps("firstName", "form-input", {
                      required: true,
                    })}
                  />
                  {showError("firstName")}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="insurance-last">
                    {t("NUTRITION_INSDISC_LBL_LAST_NAME")}{" "}
                    <RegistrationRequiredMark />
                  </label>
                  <input
                    id="insurance-last"
                    name="lastName"
                    type="text"
                    placeholder={t("NUTRITION_INSDISC_PLACEHOLDER_LAST_NAME")}
                    value={values.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoComplete="family-name"
                    {...fieldControlProps("lastName", "form-input", {
                      required: true,
                    })}
                  />
                  {showError("lastName")}
                </div>

                <div className="form-group insurance-details-span-2">
                  <label className="form-label" htmlFor="insurance-dob">
                    {t("DATE_OF_BIRTH")}{" "}
                    <RegistrationRequiredMark />
                  </label>
                  <RegistrationDobField
                    id="insurance-dob"
                    name="dob"
                    value={values.dob}
                    onChange={(formatted, { touched }) => {
                      if (touched) {
                        setFieldTouched("dob", true);
                      }
                      setFieldValue("dob", formatted);
                    }}
                    onBlur={handleBlur}
                    {...fieldControlProps("dob", undefined, {
                      required: true,
                    })}
                  />
                  <div className="form-hint">
                    {t("NUTRITION_REGISTER_HINT_DOB_FORMAT", {
                      defaultValue: "Use MM/DD/YYYY",
                    })}
                  </div>
                  {showError("dob")}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="insurance-member-id">
                    {t("MEMBER_ID")} <RegistrationRequiredMark />
                  </label>
                  <input
                    id="insurance-member-id"
                    name="memberId"
                    type="text"
                    placeholder={t("NUTRITION_INSMANUAL_PLACEHOLDER_MEMBER_ID")}
                    value={values.memberId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    {...fieldControlProps("memberId", "form-input", {
                      required: true,
                      describedBy: "insurance-member-id-hint",
                    })}
                  />
                  <div id="insurance-member-id-hint" className="form-hint">
                    {t("NUTRITION_INSDISC_HINT_MEMBER_ID_CARD")}
                  </div>
                  {showError("memberId")}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="insurance-group-number">
                    {t("NUTRITION_COMMON_LBL_GROUP_NUMBER")}
                  </label>
                  <input
                    id="insurance-group-number"
                    name="groupNumber"
                    type="text"
                    placeholder={t("NUTRITION_INSMANUAL_PLACEHOLDER_GROUP_NUMBER")}
                    value={values.groupNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    {...fieldControlProps("groupNumber", "form-input")}
                  />
                  {showError("groupNumber")}
                </div>

                <div className="form-group insurance-details-span-2">
                  <label className="form-label" htmlFor="insurance-relationship">
                    {t("NUTRITION_INSDISC_LBL_RELATIONSHIP")}{" "}
                    <RegistrationRequiredMark />
                  </label>
                  <RegistrationFormSelect
                    id="insurance-relationship"
                    name="relationship"
                    value={values.relationship}
                    onChange={handleRelationshipChange}
                    onBlur={handleBlur}
                    {...fieldControlProps("relationship", undefined, {
                      required: true,
                    })}
                  >
                    {RELATIONSHIP_OPTIONS.map((opt) => (
                      <option key={opt.value || "placeholder"} value={opt.value}>
                        {getInsuranceOptionLabel(opt, t)}
                      </option>
                    ))}
                  </RegistrationFormSelect>
                  {showError("relationship")}
                </div>

                {showPolicyHolderFields && (
                  <>
                    <div className="form-group">
                      <label
                        className="form-label"
                        htmlFor="insurance-policy-holder-first"
                      >
                        {t("NUTRITION_INSMANUAL_LBL_HOLDER_FIRST_NAME")}{" "}
                        <RegistrationRequiredMark />
                      </label>
                      <input
                        id="insurance-policy-holder-first"
                        name="policyHolderFirstName"
                        type="text"
                        placeholder={t("NUTRITION_INSMANUAL_PLACEHOLDER_FIRST_NAME")}
                        value={values.policyHolderFirstName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        autoComplete="given-name"
                        {...fieldControlProps("policyHolderFirstName", "form-input", {
                          required: true,
                        })}
                      />
                      {showError("policyHolderFirstName")}
                    </div>

                    <div className="form-group">
                      <label
                        className="form-label"
                        htmlFor="insurance-policy-holder-last"
                      >
                        {t("NUTRITION_INSMANUAL_LBL_HOLDER_LAST_NAME")}{" "}
                        <RegistrationRequiredMark />
                      </label>
                      <input
                        id="insurance-policy-holder-last"
                        name="policyHolderLastName"
                        type="text"
                        placeholder={t("NUTRITION_INSMANUAL_PLACEHOLDER_LAST_NAME")}
                        value={values.policyHolderLastName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        autoComplete="family-name"
                        {...fieldControlProps("policyHolderLastName", "form-input", {
                          required: true,
                        })}
                      />
                      {showError("policyHolderLastName")}
                    </div>

                    <div className="form-group insurance-details-span-2">
                      <label
                        className="form-label"
                        htmlFor="insurance-policy-holder-dob"
                      >
                        {t("NUTRITION_INSMANUAL_LBL_HOLDER_DOB")}{" "}
                        <RegistrationRequiredMark />
                      </label>
                      <RegistrationDobField
                        id="insurance-policy-holder-dob"
                        name="policyHolderDob"
                        value={values.policyHolderDob}
                        onChange={(formatted, { touched: dobTouched }) => {
                          if (dobTouched) {
                            setFieldTouched("policyHolderDob", true);
                          }
                          setFieldValue("policyHolderDob", formatted);
                        }}
                        onBlur={handleBlur}
                        {...fieldControlProps("policyHolderDob", undefined, {
                          required: true,
                        })}
                      />
                      <div className="form-hint">
                        {t("NUTRITION_REGISTER_HINT_DOB_FORMAT", {
                          defaultValue: "Use MM/DD/YYYY",
                        })}
                      </div>
                      {showError("policyHolderDob")}
                    </div>
                  </>
                )}

                <RegistrationInsuranceCardUpload
                  forceShowErrors={submitCount > 0 || cardImagesSubmitAttempted}
                  idPrefix="verify-insurance-card"
                  onImagesChange={setHasCardImages}
                />
              </div>

              <div className="divider" />

              <div className="notice-box notice-box--coverage">
                <div className="notice-icon" aria-hidden>
                  🔒
                </div>
                <div className="notice-text">
                  {t("NUTRITION_INSDISC_DESC_NOTICE")}
                </div>
              </div>

              <button
                type="submit"
                className="btn-primary"
                disabled={isSubmitting || insuranceProvidersLoading}
              >
                {t("NUTRITION_INSDISC_BTN_CHECK")}
              </button>
              <button
                type="button"
                className="btn-ghost"
                onClick={handleManualEntry}
              >
                {t("NUTRITION_INSDISC_BTN_MANUAL")}
              </button>
            </form>
          </div>
        </div>
      </RegistrationLayout>
      <InsuranceDetailsConfirmModal
        isOpen={isConfirmOpen}
        details={confirmDetails}
        confirmLabel={t("NUTRITION_INSDISC_BTN_CHECK")}
        onConfirm={confirmCheckCoverage}
        onClose={() => setIsConfirmOpen(false)}
        onEdit={() => {
          setIsConfirmOpen(false);
          scheduleFocusRegistrationField("insurance-payer");
        }}
      />
    </RegistrationSetupWrapper>
  );
};

export default compose(
  withRouter,
  enhancer
)(RegistrationInsuranceVerification);
